"use strict";
/**
 * Market Selection Service
 * Handles event and market selection logic
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.selectEvent = selectEvent;
exports.openSportSwitchModal = openSportSwitchModal;
exports.handleEventTypeSelection = handleEventTypeSelection;
exports.switchMarket = switchMarket;
const state_1 = require("../state");
const markets_1 = require("../markets");
const ui_1 = require("../ui");
const pricing_1 = require("../handlers/order/baseball/pricing");
const order_buttons_1 = require("../handlers/order/basketball/order-buttons");
const order_buttons_2 = require("../handlers/order/soccer/order-buttons");
const api_1 = require("../api");
const event_type_service_1 = require("./event-type-service");
const parallel_markets_service_1 = require("./parallel-markets-service");
const capabilities_1 = require("../venues/capabilities");
const market_order_1 = require("../markets/market-order");
const market_filter_1 = require("../markets/market-filter");
const primary_market_filter_1 = require("../ui/primary-market-filter");
const primary_market_filter_2 = require("./primary-market-filter");
// Pending event ID waiting for type selection
let pendingEventId = null;
// Whether we're switching sport type for an existing event (vs. first-time selection)
let isSportSwitch = false;
/**
 * Handle event selection
 * When a user clicks an event:
 * 1. Check if we have a saved event type for today
 * 2. If not, show event type modal first
 * 3. Fetch ALL markets for that event
 * 4. Store markets in state for switching
 * 5. Select the alphabetically first market
 * 6. Render the primary market list
 * 7. Navigate to order page
 * 8. Display market details
 */
async function selectEvent(eventId) {
    // Check if event type is already saved for today
    const existingType = (0, event_type_service_1.getEventType)(eventId);
    if (!existingType) {
        // Show event type modal and wait for selection
        pendingEventId = eventId;
        isSportSwitch = false;
        (0, ui_1.showEventTypeModal)();
        return;
    }
    // Event type already known, proceed directly
    await proceedWithEventSelection(eventId);
}
/**
 * Open the event type modal to switch sport for the current event.
 * Called when the user clicks the event type indicator in the order page header.
 */
function openSportSwitchModal() {
    const state = (0, state_1.getState)();
    const eventId = state.selectedEventTicker;
    if (!eventId)
        return;
    pendingEventId = eventId;
    isSportSwitch = true;
    (0, ui_1.showEventTypeModal)();
}
/**
 * Called when user picks an event type from the modal.
 */
function handleEventTypeSelection(eventType) {
    if (!pendingEventId)
        return;
    const eventId = pendingEventId;
    const switching = isSportSwitch;
    pendingEventId = null;
    isSportSwitch = false;
    (0, ui_1.hideEventTypeModal)();
    // Save the event type
    (0, event_type_service_1.saveEventType)(eventId, eventType);
    if (switching) {
        // We're switching sport type on an already-selected event.
        // Re-apply settings without re-fetching markets.
        applySportSettings(eventId, eventType);
    }
    else {
        // Proceed with event selection
        proceedWithEventSelection(eventId);
    }
}
/**
 * Manual price mode default per event type: ON for every real sport
 * (baseball, football, hockey, soccer), OFF for "other" and for events
 * with no saved type. Basketball is the exception — it defaults OFF and
 * is handled by `applyBasketballSettings`, not this helper.
 */
function defaultManualPriceMode(eventType) {
    return eventType != null && eventType !== "other";
}
/**
 * Apply sport-specific settings after a sport switch on an existing event.
 */
function applySportSettings(eventId, eventType) {
    if (eventType === "baseball") {
        applyBaseballSettings();
        applyInningState(eventId);
    }
    else if (eventType === "basketball") {
        applyBasketballSettings();
        (0, ui_1.updateInningVisibility)(false);
    }
    else if (eventType === "soccer") {
        applySoccerSettings();
        (0, ui_1.updateInningVisibility)(false);
    }
    else {
        disableSportModes(defaultManualPriceMode(eventType));
        (0, ui_1.updateInningVisibility)(false);
    }
    // Update indicator to show new sport
    (0, ui_1.updateEventTypeIndicator)(eventType);
}
/**
 * Set the sport-mode flags (mutually exclusive) plus the per-event-type
 * manual-price default, and refresh the dependent UI.
 * All layout visibility flows through the resolver, so `forceDefaultButtons`
 * wins here regardless of which sport was selected.
 */
function setSportModes(baseballMode, basketballMode, soccerMode, manualPriceMode) {
    const state = (0, state_1.getState)();
    if (state.tradingSettings.baseballMode !== baseballMode ||
        state.tradingSettings.basketballMode !== basketballMode ||
        state.tradingSettings.soccerMode !== soccerMode ||
        state.tradingSettings.manualPriceMode !== manualPriceMode) {
        (0, state_1.updateState)({
            tradingSettings: {
                ...state.tradingSettings,
                baseballMode,
                basketballMode,
                soccerMode,
                manualPriceMode,
            },
        });
        (0, state_1.saveSettingsToStorage)();
    }
    const settings = (0, state_1.getState)().tradingSettings;
    (0, ui_1.updateOrderLayoutVisibility)(settings);
    (0, ui_1.updateSlippageVisibility)(settings);
    // Manual-price surfaces: in combined mode (sport layout + manual price)
    // the global overlay is hidden in favour of per-button steppers, and the
    // button hints switch between slippage % and manual price ¢.
    const isCombined = (0, ui_1.isCombinedPricingMode)(settings);
    (0, ui_1.updateManualPriceVisibility)(settings.manualPriceMode === true && !isCombined);
    (0, ui_1.updateBaseballManualPriceDisplay)(isCombined);
    if (settings.baseballMode === true) {
        (0, pricing_1.refreshAllHints)();
    }
    if (settings.basketballMode === true) {
        (0, order_buttons_1.refreshBasketballHints)();
    }
    if (settings.soccerMode === true) {
        (0, order_buttons_2.refreshSoccerHints)();
    }
}
/**
 * Apply baseball mode settings when entering a baseball event.
 * Manual price mode defaults ON for sport events.
 */
function applyBaseballSettings() {
    setSportModes(true, false, false, true);
    (0, ui_1.updateBaseballFactorDisplay)((0, state_1.getState)().tradingSettings.baseballFactor ?? 0);
}
/**
 * Apply basketball mode settings when entering a basketball event.
 * Manual price mode defaults OFF for basketball (unlike other sports).
 */
function applyBasketballSettings() {
    setSportModes(false, true, false, false);
    (0, ui_1.updateBasketballFactorDisplay)((0, state_1.getState)().tradingSettings.baseballFactor ?? 0);
}
/**
 * Apply soccer mode settings when entering a soccer event.
 * Manual price mode defaults ON for sport events.
 */
function applySoccerSettings() {
    setSportModes(false, false, true, true);
    (0, ui_1.updateSoccerFactorDisplay)((0, state_1.getState)().tradingSettings.baseballFactor ?? 0);
}
/**
 * Disable all sport button layouts (non-layout sports, "other", or
 * unsupported event types). `manualPriceMode` still applies the per-event-type
 * default: ON for real sports without their own layout (football, hockey),
 * OFF for "other".
 */
function disableSportModes(manualPriceMode) {
    setSportModes(false, false, false, manualPriceMode);
}
/**
 * Apply the stored inning/offense state for baseball events.
 */
function applyInningState(eventId) {
    const entry = (0, event_type_service_1.getEventType)(eventId);
    if (entry && entry.type === "baseball") {
        (0, ui_1.updateInningVisibility)(true);
        (0, ui_1.updateInningDisplay)(entry.inning, entry.halfInning);
        (0, ui_1.updateHalfInningIndicator)(entry.halfInning);
        (0, ui_1.updateBaseballOffenseDefense)(entry.offenseTeam);
        (0, ui_1.updateResetOffenseVisibility)(entry.offenseTeam !== null);
    }
    else {
        (0, ui_1.updateInningVisibility)(false);
    }
}
/**
 * Core event selection logic (after event type is known).
 */
async function proceedWithEventSelection(eventId) {
    try {
        // Show loading state
        (0, ui_1.showLoadingSpinner)("Loading market details...");
        // Fetch all markets for this event
        const allMarkets = await (0, markets_1.fetchEventMarkets)(eventId);
        if (!allMarkets || allMarkets.length === 0) {
            (0, ui_1.hideLoadingSpinner)();
            (0, ui_1.showError)("No markets available for this event. Please try another event.", "market-error");
            return;
        }
        const activeMarkets = (0, market_order_1.orderEventMarkets)(allMarkets.filter((m) => m.status !== "finalized"), (0, capabilities_1.marketOrder)((0, state_1.getState)().dataSource));
        if (activeMarkets.length === 0) {
            (0, ui_1.hideLoadingSpinner)();
            (0, ui_1.showError)("No active markets available for this event.", "market-error");
            return;
        }
        // Open on the first market of the operator's preferred type, when the
        // event has it (services/primary-market-filter.ts).
        const filter = (0, primary_market_filter_2.resetPrimaryFilterForEvent)(activeMarkets);
        const selectedMarket = (0, market_filter_1.filterMarkets)(activeMarkets, filter)[0] ?? activeMarkets[0];
        // Update app state with all markets and selected market
        // State change will trigger handleStateChange() which manages polling
        (0, state_1.updateState)({
            selectedEventTicker: eventId,
            availableMarkets: activeMarkets,
            selectedMarket,
            parallelMarkets: (0, parallel_markets_service_1.getParallelMarkets)(selectedMarket.id),
            currentPage: "order",
        });
        // Auto-enable the sport button layout matching the event type; manual
        // price mode defaults ON for every sport except basketball, OFF for
        // basketball and "other".
        const eventEntry = (0, event_type_service_1.getEventType)(eventId);
        if (eventEntry && eventEntry.type === "baseball") {
            applyBaseballSettings();
            applyInningState(eventId);
            (0, ui_1.updateEventTypeIndicator)("baseball");
        }
        else if (eventEntry && eventEntry.type === "basketball") {
            applyBasketballSettings();
            (0, ui_1.updateInningVisibility)(false);
            (0, ui_1.updateEventTypeIndicator)("basketball");
        }
        else if (eventEntry && eventEntry.type === "soccer") {
            applySoccerSettings();
            (0, ui_1.updateInningVisibility)(false);
            (0, ui_1.updateEventTypeIndicator)("soccer");
        }
        else {
            disableSportModes(defaultManualPriceMode(eventEntry?.type));
            (0, ui_1.updateInningVisibility)(false);
            (0, ui_1.updateEventTypeIndicator)(eventEntry?.type ?? null);
        }
        // Clear previous order details
        (0, ui_1.clearOrderDetails)();
        renderPrimaryPicker(activeMarkets, selectedMarket.id);
        // Show/hide prev/next navigation buttons
        (0, ui_1.updateMarketNavButtons)(activeMarkets.length);
        (0, ui_1.hideLoadingSpinner)();
        console.log(`Event ${eventId} selected with ${activeMarkets.length} markets. Selected: ${selectedMarket.id}`);
    }
    catch (error) {
        console.error("Failed to load markets for event:", error);
        (0, ui_1.hideLoadingSpinner)();
        // Check for secret key error
        if ((0, api_1.isSecretKeyError)(error)) {
            (0, ui_1.showReAuthModal)();
            (0, ui_1.showError)("Invalid secret key. Please re-enter it.", "market-error");
        }
        else {
            (0, ui_1.showError)(error instanceof Error
                ? error.message
                : "Failed to load market details. Please try another event.", "market-error");
        }
    }
}
/** The Primary Market list and its filter row, for this event's markets. */
function renderPrimaryPicker(markets, selectedId) {
    const filter = (0, primary_market_filter_2.getPrimaryFilter)();
    (0, ui_1.renderMarketSwitcher)(markets, selectedId, filter);
    (0, primary_market_filter_1.renderPrimaryMarketFilter)(markets, filter, {
        onCategory: (category) => {
            (0, primary_market_filter_2.setPrimaryFilterCategory)(category);
            refreshPrimaryPicker();
        },
        onText: (text) => {
            (0, primary_market_filter_2.setPrimaryFilterText)(text);
            refreshPrimaryPicker();
        },
    });
}
/** Redraw the picker after a filter change. Never switches the market. */
function refreshPrimaryPicker() {
    const { availableMarkets, selectedMarket } = (0, state_1.getState)();
    if (!availableMarkets || !selectedMarket)
        return;
    renderPrimaryPicker(availableMarkets, selectedMarket.id);
}
/**
 * Handle market switching on the order page
 * When the user taps a market in the list, or presses ‹ ›
 */
async function switchMarket(newMarketId) {
    try {
        const state = (0, state_1.getState)();
        // Validate that market is in available markets
        const newMarket = state.availableMarkets?.find((m) => m.id === newMarketId);
        if (!newMarket) {
            (0, ui_1.showOrderError)("Selected market is not available. Please refresh the page.");
            return;
        }
        // Update selected market in state
        // handleStateChange() will detect this change and manage polling
        (0, state_1.updateState)({
            selectedMarket: newMarket,
            parallelMarkets: (0, parallel_markets_service_1.getParallelMarkets)(newMarket.id),
        });
        // Re-rendered, not just re-valued: a market the filter hid was listed
        // only because it was selected.
        renderPrimaryPicker(state.availableMarkets ?? [], newMarketId);
        console.log(`Switched to market: ${newMarket.id}`);
    }
    catch (error) {
        console.error("Failed to switch market:", error);
        (0, ui_1.showOrderError)(error instanceof Error
            ? error.message
            : "Failed to switch markets. Please try again.");
    }
}
